import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
  Chip,
  alpha,
  Paper
} from '@mui/material';
import { 
  CalendarMonth, 
  Science, 
  EditCalendarOutlined, 
  EventNoteRounded,
  ScheduleSendRounded 
} from '@mui/icons-material';
import { getYears } from '../../../../helpers/dateHelpers';
import { generateFacilityReport } from '../../../../helpers/api';
import SchedulerDialog from './scheduler-dialogs/SchedulerDialog';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { Empty } from 'antd';
import { reportTypes, userCats } from '../../../../helpers/appstate';

export default function FacilityScheduler({ props, facilityId }) {
  const { userCategory } = props;
  const years = getYears();

  const [quarterList, setQuarterList] = useState([]);
  const [quarterLoading, setQuarterLoading] = useState(false);
  const [quarterSelected, setQuarterSelected] = useState(null);
  const [dialogSchedule, setDialogSchedule] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    fetchQuarters();
  }, [facilityId, selectedYear]);

  useEffect(() => {
    if (refresh) {
      fetchQuarters();
    }
  }, [refresh, facilityId, selectedYear]);

  const fetchQuarters = async () => {
    setQuarterLoading(true);
    try {
      const res = await generateFacilityReport(facilityId, selectedYear);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setQuarterList(data || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setQuarterLoading(false);
      setRefresh(false);
    }
  };

  // --- RENDERING FUNCTION ---
  const renderMainContent = () => {
    if (quarterLoading) {
      return (
        <Stack spacing={2}>
          <LoaderSkeleton height={60} />
          <LoaderSkeleton height={60} />
          <LoaderSkeleton height={60} />
        </Stack>
      );
    }

    if (!quarterList || quarterList?.length < 1) {
      return (
        <Box sx={{ py: 10, textAlign: 'center', bgcolor: alpha('#f4f6f8', 0.5), borderRadius: '16px' }}>
          <Empty description="No schedules found for this year" />
        </Box>
      );
    }

    return (
      <Stack spacing={1.5}>
        {quarterList?.map((i, index) => {
          const isQAQC = i?.reportType === reportTypes.QAQC;
          const hasValidDate = i?.scheduleDate && !i.scheduleDate.startsWith('0001-01-01');

          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                p: 2,
                borderRadius: '14px',
                border: '1px solid',
                borderColor: isQAQC ? alpha('#ed6c02', 0.2) : 'divider',
                bgcolor: isQAQC ? alpha('#ed6c02', 0.02) : '#fff',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: isQAQC ? alpha('#ed6c02', 0.06) : alpha('#1976d2', 0.03),
                  borderColor: isQAQC ? '#ed6c02' : 'primary.main',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                  transform: 'translateX(4px)'
                },
              }}
            >
              {/* Left Side: Badge & Title */}
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: '10px',
                    bgcolor: isQAQC ? alpha('#ed6c02', 0.12) : alpha('#1976d2', 0.08),
                    color: isQAQC ? '#ed6c02' : '#1976d2',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.8,
                    whiteSpace: 'nowrap',
                    border: '1px solid',
                    borderColor: 'inherit'
                  }}
                >
                  {isQAQC ? <Science sx={{ fontSize: 16 }} /> : null}
                  Q{i?.reportQuarter}
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
                    {i?.reportTitle || `Quarter ${i?.reportQuarter} Report`}
                  </Typography>
                  {isQAQC ? (
                    <Typography variant="caption" color="warning.main" fontWeight={700} sx={{ mt: 0.5, display: 'block' }}>
                      Reserved for QAQC
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.disabled" fontWeight={500}>
                      Standard Monitoring Period
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Right Side: Date & Actions */}
              <Stack direction="row" spacing={2} alignItems="center">
                {hasValidDate && (
                  <Chip
                    icon={<CalendarMonth sx={{ fontSize: '16px !important' }} />}
                    label={`Monitoring: ${new Date(i?.scheduleDate).toLocaleDateString('en-NG', { dateStyle: 'medium' })}`}
                    variant="outlined"
                    sx={{
                      borderRadius: '10px',
                      borderColor: alpha('#000', 0.1),
                      bgcolor: '#ffffff',
                      color: 'text.primary',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      height: 32
                    }}
                  />
                )}

                <Box>
                  {isQAQC ? (
                    userCategory === userCats.EDM && (
                      <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        startIcon={hasValidDate ? <EditCalendarOutlined /> : <ScheduleSendRounded />}
                        onClick={() => {
                          setQuarterSelected(i);
                          setDialogSchedule(true);
                        }}
                        sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, bgcolor: 'info.main' }}
                      >
                        {hasValidDate ? 'Reschedule' : 'Schedule'}
                      </Button>
                    )
                  ) : (
                    userCategory === userCats.CONSULTANT && (
                      <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        startIcon={hasValidDate ? <EditCalendarOutlined /> : <ScheduleSendRounded />}
                        onClick={() => {
                          setQuarterSelected(i);
                          setDialogSchedule(true);
                        }}
                        sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, bgcolor: 'info.main' }}
                      >
                        {hasValidDate ? 'Reschedule' : 'Schedule'}
                      </Button>
                    )
                  )}

                  {userCategory === userCats.PERSONNEL && !hasValidDate && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', pr: 1 }}>
                        Not Scheduled
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', mb: 3, alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: alpha('#1976d2', 0.1), color: 'primary.main', width: 40, height: 40 }}>
            <EventNoteRounded />
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
                Monitoring Schedules
            </Typography>
            <Typography variant="body2" color="textSecondary">
                Audit timelines and QAQC status for {selectedYear}
            </Typography>
        </Box>

        <Select
          value={selectedYear}
          size="small"
          onChange={(e) => setSelectedYear(e.target.value)}
          sx={{ 
            borderRadius: '10px', 
            bgcolor: '#f9fafb', 
            minWidth: 100,
            fontWeight: 700 
          }}
        >
          {years?.map((year) => (
            <MenuItem key={year} value={year} sx={{ fontWeight: 600 }}>
              {year}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* FIXED CALL TO FUNCTION */}
      {renderMainContent()}

      {dialogSchedule && (
        <SchedulerDialog
          open={dialogSchedule}
          onClose={() => {
            setDialogSchedule(false);
            setRefresh(true);
          }}
          facilityId={facilityId}
          quarter={quarterSelected}
          year={selectedYear}
        />
      )}
    </Box>
  );
}